import { NextResponse } from "next/server";
import createMiddleware from "next-intl/middleware";
import { catalogueCountries, COUNTRIES } from "./constants/common/common";
import { getProductByUrlAPI } from "./api/pdpApi";

const isLocalMachine = process.env.NEXT_PUBLIC_IS_LOCAL_MACHINE === "true";

/**
 *
 * @param {object} req
 * @returns
 */
const isMobileDevice = (req) => {
  const userAgent = req.headers.get("user-agent") || "";
  const isMobile = /mobile/i.test(userAgent);
  return isMobile;
};

/**
 * Determines the environment type based on the given hostname.
 *
 * This function inspects the hostname and returns a short environment
 * identifier string such as `"np"`, `"uat"`, or `"prod"`.
 *
 * @param {string} hostName - The hostname string (e.g., "qa-example.com", "uat-example.com", "www.example.com").
 * @returns {"np" | "uat" | "prod"} The resolved environment type.
 *
 */
export const returnEnvType = (hostName) => {
  if (!hostName || typeof hostName !== "string") {
    return "prod";
  }

  const hostnameLower = hostName.toLowerCase();

  // Check localhost and local dev domains (e.g. dev-redroses.fnpplus.com)
  if (/localhost|127\.0\.0\.1|fnpplus\.com/i.test(hostnameLower)) {
    return "uat";
  }

  // Check UAT environment FIRST (before QA to avoid conflicts)
  // Matches: uat-new.fnp.com, uat.fnp.com, new-uat.fnp.com, etc.
  if (/uat/i.test(hostnameLower) && !/^qa-/i.test(hostnameLower)) {
    return "uat";
  }

  // Check QA environment (explicit qa- prefix)
  if (/^qa-/i.test(hostnameLower)) {
    return "np";
  }

  // Check production (www)
  if (/^www\./i.test(hostnameLower)) {
    return "prod";
  }

  // Default to prod
  return "prod";
};

/**
 * Returns true when the pathname is the homepage (`/`) or msite store home (`/store`, `/store/[slug]`).
 *
 * @param {string} pathName - The request pathname.
 * @returns {boolean} Whether this is a homepage (or msite store home) route.
 */
export const isHomePage = (pathName) => pathName === "/" || pathName === "/store" || pathName.startsWith("/store/");

/**
 * Determines whether the given pathname corresponds to a valid page type.
 *
 * This function checks the provided pathname against a list of excluded paths
 * such as favicon, homepage, and specific content sections (e.g., gift, info, quotes).
 * If the pathname matches any of these excluded paths, it returns `false`.
 * Otherwise, it returns `true`.
 *
 * @param {string} pathName - The request pathname (e.g., "/flowers/roses").
 * @returns {boolean} `true` if the pathname is considered a product page type, otherwise `false`.
 *
 */
export const checkPageTypeForEdgeCache = (pathName) => {
  switch (true) {
    case pathName === "/favicon.ico":
    case isHomePage(pathName):
    case pathName.includes("/gift/"):
    case pathName.includes("/info/"):
    case pathName.includes("/quotes/"):
    case pathName.includes("/services/"):
    case pathName.includes("/offers/"):
    case pathName.includes("/ideas/"):
    case pathName.includes("/article/"):
    case pathName.includes("/shayari/"):
      return false;

    default:
      return true;
  }
};

/**
 * Checks if the pathname is a PDP page.
 *
 * @param {string} pathName - The request pathname.
 * @returns {boolean} `true` if the pathname is a PDP page, otherwise `false`.
 */
export const isPDPPage = (pathName) => {
  return pathName.includes("/gift/") && pathName !== "/gift";
};

/**
 * Extracts country and urlIdentifier from PDP pathname.
 *
 * @param {string} pathName - The request pathname (e.g., "/gift/product-slug" or "/singapore/gift/product-slug").
 * @returns {{country: string, urlIdentifier: string}} Object containing country and urlIdentifier.
 */
export const extractPDPParams = (pathName) => {
  const pathNameArr = pathName.split("/").filter(Boolean);
  let country = COUNTRIES.INDIA;
  let urlIdentifier = "";

  if (pathNameArr[0] === "gift") {
    // Format: /gift/product-slug
    country = COUNTRIES.INDIA;
    urlIdentifier = pathNameArr.slice(1).join("/");
  } else if (catalogueCountries.has(pathNameArr[0]) && pathNameArr[1] === "gift") {
    // Format: /country/gift/product-slug
    country = pathNameArr[0];
    urlIdentifier = pathNameArr.slice(2).join("/");
  }

  return { country, urlIdentifier };
};

/**
 * Creates an Edge Cache Tag string for PDP pages based on product data.
 *
 * @param {string} pathName - The request pathname.
 * @param {string} hostname - The current request hostname (used to resolve env type).
 * @param {Object} productData - The product data from API response.
 * @returns {string} A comma-separated string containing the cache tags.
 */
export const createPDPEdgeCacheTag = (pathName, hostname, productId) => {
  const pathNameArr = pathName.split("/").filter(Boolean);
  const catalogueId = catalogueCountries.has(pathNameArr[0]) ? pathNameArr[0] : COUNTRIES.INDIA;
  const envType = returnEnvType(hostname);
  const globalPDPEdgeCacheTag = `${envType}-pr-${catalogueId}`;
  const pdpEdgeCacheTag = `${envType}-pr-${productId}`;
  return `${globalPDPEdgeCacheTag}, ${pdpEdgeCacheTag}`;
};

/**
 * Creates an Edge Cache Tag string based on the given path and hostname.
 *
 * @param {string} pathName - The request pathname (e.g., "/in/flowers-lp").
 * @param {string} hostname - The current request hostname (used to resolve env type).
 * @returns {string} A comma-separated string containing the global and path-specific cache tags.
 */
export const createEdgeCacheTag = (pathName, hostname) => {
  const pathNameArr = pathName.split("/").filter(Boolean);
  const catalogueId = catalogueCountries.has(pathNameArr[0]) ? pathNameArr[0] : COUNTRIES.INDIA;
  let finalEdgeCacheTag = null;
  const globalPlpEdgeCacheTag = `com-${returnEnvType(hostname)}-ct-${catalogueId}`;
  const plpEdgeCacheTag = `com-${returnEnvType(hostname)}-ct-${pathNameArr.join("-").replace("-lp", "")}`;
  finalEdgeCacheTag = `${globalPlpEdgeCacheTag}, ${plpEdgeCacheTag}`;
  return finalEdgeCacheTag;
};

/**
 * Creates an Edge Cache Tag for the homepage, split by device.
 * Production examples: `com-prod-d-home` (desktop), `com-prod-m-home` (mobile).
 *
 * @param {string} hostname - The current request hostname (used to resolve env type).
 * @param {boolean} isMobile - Whether the request is from a mobile device.
 * @returns {string} Device-specific homepage edge cache tag.
 */
export const createHomeEdgeCacheTag = (hostname, isMobile) => {
  const device = isMobile ? "m" : "d";
  return `com-${returnEnvType(hostname)}-${device}-home`;
};

/**
 *
 * @param {object} request
 * @returns
 */
export async function middleware(request) {
  const { pathname } = request.nextUrl;

  // Local dev only: skip i18n middleware so API proxy routes are reachable.
  if (isLocalMachine && (pathname.startsWith("/control") || pathname.startsWith("/apigw/serviceability"))) {
    return NextResponse.next();
  }

  // Page routes only render on GET/HEAD. Any other method (usually a stray
  // browser-default <form> submission with Enter key) has no route handler and
  // crashes Next.js into /_error with a 500. Short-circuit here with 405.
  if (request.method !== "GET" && request.method !== "HEAD") {
    return new Response(null, {
      status: 405,
      headers: { Allow: "GET, HEAD" },
    });
  }

  // Get actual hostname from headers (for containerized/proxied environments)
  // Check x-forwarded-host first (set by proxies/load balancers), then host header, then fallback to nextUrl.hostname
  const forwardedHost = request.headers.get("x-forwarded-host");
  const hostHeader = request.headers.get("host");
  const hostname = forwardedHost || hostHeader || request.nextUrl.hostname;

  let finalEdgeCacheTag = null;

  // Clone the request headers and set a new header `x-hello-from-middleware1`
  const requestHeaders = new Headers(request.headers);
  const isMobile = isMobileDevice(request);
  isMobile ? requestHeaders.set("x-device-type", "mobile") : requestHeaders.set("x-device-type", "desktop");
  const handleI18nRouting = createMiddleware({
    // A list of all locales that are supported
    locales: ["en", "de", "cs", "fr"],
    // Used when no locale matches
    defaultLocale: "en",
    localePrefix: "never",
  });

  const response = handleI18nRouting(request);

  // Handle PDP pages with API call for edge cache tags
  if (isPDPPage(pathname)) {
    const { country, urlIdentifier } = extractPDPParams(pathname);
    if (urlIdentifier) {
      try {
        const productData = await getProductByUrlAPI(country, urlIdentifier);
        // Edge case: Validate productData and productId before creating cache tag
        if (productData?.productId) {
          try {
            finalEdgeCacheTag = createPDPEdgeCacheTag(pathname, hostname, productData.productId);
          } catch (cacheTagError) {
            // Edge case: If createPDPEdgeCacheTag throws, log but don't break middleware
            console.log("Error creating PDP edge cache tag:", cacheTagError);
            // Continue without cache tag - page will still work
          }
        }
      } catch (apiError) {
        // Edge case: If API call throws unexpected error, log but don't break middleware
        console.log("Error in getProductByUrlAPI middleware:", apiError);
        // Continue without cache tag - page will still work
      }
    }
  } else if (isHomePage(pathname)) {
    try {
      finalEdgeCacheTag = createHomeEdgeCacheTag(hostname, isMobile);
    } catch (cacheTagError) {
      console.log("Error creating home edge cache tag:", cacheTagError);
    }
  } else if (checkPageTypeForEdgeCache(pathname)) {
    try {
      finalEdgeCacheTag = createEdgeCacheTag(pathname, hostname);
    } catch (cacheTagError) {
      // Edge case: If createEdgeCacheTag throws, log but don't break middleware
      console.log("Error creating edge cache tag:", cacheTagError);
      // Continue without cache tag - page will still work
    }
  }

  if (finalEdgeCacheTag) {
    response.headers.set("edge-cache-tag", finalEdgeCacheTag);
  }

  return response;
}

export const config = {
  // Match only internationalized pathnames
  matcher: "/((?!_next|_next/image|icons|_next/static).*)",
};
